
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { X, Menu, LogOut } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from '@/components/ui/button';

/**
 * NavigationBar component for the landing page
 * Displays the application logo, navigation links, and action buttons
 */
const NavigationBar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSignOut = async () => {
    try {
      await signOut();
      // Navigation is handled in AuthContext
      toast({
        title: "Signed out successfully",
        description: "You have been signed out of your account.",
      });
    } catch (error) {
      console.error("Error during sign out:", error);
      toast({
        title: "Error signing out",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <nav className="border-b border-gray-800/50 bg-gray-900/95 backdrop-blur-md sticky top-0 z-40">
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center">
              <span className="font-inter font-bold text-2xl text-blue-400">AX</span>
            </Link>
          </div>
          
          {/* Navigation links removed as requested */}
          <div className="hidden md:flex items-center space-x-10">
            {/* Empty - all links moved to footer */}
          </div>
          
          <div className="flex items-center gap-6">
            {user ? (
              <div className="flex items-center space-x-4">
                <Link to="/chat" className="text-gray-300 hover:text-white text-sm transition-colors duration-300 px-5 py-2 rounded-xl border border-transparent hover:border-blue-800/50 hover:bg-blue-500/10 backdrop-blur-sm">
                  Go to App
                </Link>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="text-gray-300 hover:text-white transition-colors duration-300">
                      <LogOut className="h-5 w-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={handleSignOut}>
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : (
              <>
                <Link to="/auth" className="text-gray-300 hover:text-white text-sm hidden sm:inline-block transition-colors duration-300 px-5 py-2 rounded-xl border border-transparent hover:border-blue-800/50 hover:bg-blue-500/10 backdrop-blur-sm">
                  Sign In
                </Link>
                <Link to="/auth" className="bg-blue-600 hover:bg-blue-500 text-white rounded-xl px-5 py-2 text-sm font-medium transition-all duration-300 hover:shadow-lg hover:shadow-blue-600/20 transform hover:-translate-y-1">
                  Try Free
                </Link>
              </>
            )}
          </div>
          
          {/* Mobile menu button */}
          <button 
            className="md:hidden text-gray-300 hover:text-white transition-colors duration-300"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? 
              <X className="h-6 w-6" /> : 
              <Menu className="h-6 w-6" />
            }
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-gray-900/95 backdrop-blur-md border-b border-gray-800/50">
          <div className="container mx-auto px-4 py-4 space-y-4">
            {user && (
              <Link 
                to="/chat" 
                className="block text-center text-gray-300 hover:text-white text-base py-2 px-4 rounded-xl border border-gray-800/50 hover:bg-gray-800/50"
                onClick={() => setMobileMenuOpen(false)}
              >
                Go to App
              </Link>
            )}
            {/* Mobile menu links removed */}
            <div className="pt-4 border-t border-gray-800/50 flex flex-col gap-3">
              {user ? (
                <Button
                  onClick={handleSignOut}
                  className="w-full flex items-center gap-3 justify-center rounded-xl"
                  variant="destructive"
                >
                  <LogOut className="h-5 w-5" />
                  <span>Logout</span>
                </Button>
              ) : (
                <>
                  <Link 
                    to="/auth" 
                    className="block text-center text-gray-300 hover:text-white text-base py-2 px-4 rounded-xl border border-gray-800/50 hover:bg-gray-800/50"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Sign In
                  </Link>
                  <Link 
                    to="/auth" 
                    className="block text-center bg-blue-600 hover:bg-blue-500 text-white rounded-xl py-2 px-4 text-base font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Try Free
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default NavigationBar;
